import React, { useEffect, useState } from "react";
import {
  FaTimes,
  FaExclamationTriangle,
  FaSpinner,
  FaTrash,
  FaCheckCircle,
} from "react-icons/fa";
import apiRequest from "../services/api";

export default function InvoiceCancelModal({
  isOpen,
  invoice,
  onClose,
  onCancelSuccess,
}) {
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setNote("");
      setError("");
    }
  }, [isOpen, invoice]);

  if (!isOpen || !invoice) return null;

  const handleCancel = async (e) => {
    e.preventDefault();

    if (!note.trim()) {
      setError("Cancellation note is required.");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const queryParams = new URLSearchParams({
        note: note.trim(),
      }).toString();

      const result = await apiRequest(
        `invoices/${invoice.id}/cancel?${queryParams}`,
        {
          method: "PUT",
        }
      );

      if (result && result.success === false) {
        throw new Error(result.message || "Failed to cancel invoice.");
      }

      onCancelSuccess?.({
        ...invoice,
        status: "CANCELLED",
      });

      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to cancel invoice."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center p-3 sm:p-6 font-sans">
      {/* Modal */}
      <div className="relative w-full max-w-xl bg-slate-950/95 border border-white/[0.08] backdrop-blur-2xl rounded-2xl p-5 sm:p-6 shadow-2xl text-slate-100 overflow-hidden">
        {/* Background Glow */}
        <div className="absolute top-[-30%] right-[-10%] w-[300px] h-[300px] rounded-full bg-rose-500/10 blur-[100px] pointer-events-none" />

        {/* Header */}
        <div className="relative flex justify-between items-center pb-4 border-b border-white/[0.08]">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400">
              <FaTrash className="text-sm" />
            </div>

            <div>
              <h3 className="text-base font-extrabold text-white tracking-tight">
                Cancel Invoice
              </h3>

              <p className="text-[11px] text-slate-400 mt-0.5 font-mono">
                Invoice:{" "}
                <span className="text-white font-semibold">
                  {invoice.invoiceNumber || "—"}
                </span>
              </p>
            </div>
          </div>

          <button
            type="button"
            disabled={isLoading}
            onClick={onClose}
            className="p-2 rounded-xl bg-white/[0.04] text-slate-400 hover:text-white hover:bg-white/[0.08] border border-transparent hover:border-white/[0.08] transition"
          >
            <FaTimes className="text-xs" />
          </button>
        </div>

        {/* Warning */}
        <div className="relative mt-4 p-3 rounded-xl bg-rose-950/30 border border-rose-500/20 flex items-start gap-3">
          <FaExclamationTriangle className="text-rose-400 mt-0.5 shrink-0" />

          <div className="text-xs">
            <span className="block font-bold text-rose-300">
              Invoice Cancellation
            </span>

            <span className="text-slate-400">
              Are you sure you want to cancel this invoice? This action should
              only be performed when the invoice is no longer valid.
            </span>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="relative mt-4 p-3 bg-rose-950/40 border border-rose-500/20 rounded-xl text-rose-400 text-xs flex items-center gap-2">
            <FaExclamationTriangle className="shrink-0 text-sm" />
            <span className="break-words font-medium">{error}</span>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={handleCancel}
          className="relative flex flex-col gap-4 mt-5"
        >
          {/* Invoice Information */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
              <span className="block text-[9px] uppercase font-bold tracking-wider text-slate-500">
                Invoice
              </span>

              <span className="block mt-1 text-xs font-semibold text-white font-mono">
                {invoice.invoiceNumber || "—"}
              </span>
            </div>

            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
              <span className="block text-[9px] uppercase font-bold tracking-wider text-slate-500">
                Current Status
              </span>

              <span className="block mt-1 text-xs font-semibold text-amber-400 uppercase">
                {invoice.status || "—"}
              </span>
            </div>
          </div>

          {/* Cancellation Note */}
          <div>
            <label className="block text-[10px] uppercase font-bold tracking-wider text-slate-400 mb-2">
              Cancellation Note
              <span className="text-rose-400 ml-1">*</span>
            </label>

            <textarea
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                if (error) setError("");
              }}
              disabled={isLoading}
              maxLength={500}
              rows={4}
              placeholder="Enter the reason for cancelling this invoice..."
              className="w-full rounded-xl border border-slate-700 bg-slate-900 px-3 py-3 text-xs text-slate-100 placeholder-slate-600 resize-none focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500/30 disabled:opacity-50"
            />

            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-slate-600">
                Cancellation reason is required.
              </span>

              <span className="text-[10px] text-slate-600">
                {note.length}/500
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end items-center pt-4 border-t border-white/[0.08] mt-2">
            <button
              type="button"
              disabled={isLoading}
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 rounded-xl text-xs font-bold text-slate-400 border border-transparent hover:text-slate-200 hover:bg-white/[0.04] transition"
            >
              Keep Invoice
            </button>

            <button
              type="submit"
              disabled={isLoading || !note.trim()}
              className="w-full sm:w-auto px-5 py-2 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-500 text-white border border-rose-400/60 shadow-[0_0_15px_rgba(244,63,94,0.25)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <FaSpinner className="animate-spin text-xs" />
                  <span>Cancelling…</span>
                </>
              ) : (
                <>
                  <FaCheckCircle className="text-xs" />
                  <span>Confirm Cancellation</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
